import axios, { type AxiosInstance } from 'axios';
import {
  refreshAuthenticationSession,
  shouldAttemptTokenRefresh,
  type RetryableRequestConfig,
} from '@/api/tokenRefresh';
import { environment } from '@/config/environment';
import {
  clearAuthenticationTokens,
  readActiveWorkspaceSelection,
  readStoredAccessToken,
  SESSION_EXPIRED_EVENT_NAME,
  UNAUTHORIZED_EVENT_NAME,
} from '@/lib/authStorage';
import { REQUIRED_CONSENTS_MISSING_EVENT_NAME } from '@/lib/consentGateEvents';
import {
  DOMAIN_WORKSPACE_MISMATCH_EVENT_NAME,
  resolveWorkspaceHeaderForHost,
} from '@/lib/hostWorkspaceBinding';
import {
  DOMAIN_WORKSPACE_MISMATCH_ERROR_CODES,
  EMAIL_VERIFICATION_REQUIRED_ERROR_CODE,
  LEGAL_ACCEPTANCE_PENDING_ERROR_CODE,
  REQUIRED_CONSENTS_MISSING_ERROR_CODE,
} from '@/constants/apiErrorCodes';
import { EMAIL_VERIFICATION_REQUIRED_EVENT_NAME } from '@/lib/emailVerificationEvents';
import { LEGAL_ACCEPTANCE_PENDING_EVENT_NAME } from '@/lib/legalAcceptanceEvents';
import { resolveActiveLanguage } from '@/api/requestLanguage';

function isForbiddenResponseWithCode(requestError: unknown, errorCode: string): boolean {
  if (axios.isAxiosError(requestError) === false || requestError.response === undefined) {
    return false;
  }
  if (requestError.response.status !== 403) {
    return false;
  }
  const responseData = requestError.response.data as Record<string, unknown> | null;
  return (
    responseData !== null &&
    typeof responseData === 'object' &&
    responseData.error_code === errorCode
  );
}

function createHttpClient(): AxiosInstance {
  const httpClient = axios.create({
    baseURL: environment.apiBaseUrl,
    timeout: environment.apiTimeoutMilliseconds,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  httpClient.interceptors.request.use(function attachRequestHeaders(requestConfiguration) {
    const storedAccessToken = readStoredAccessToken();
    if (storedAccessToken.length > 0) {
      requestConfiguration.headers.set('Authorization', 'Bearer ' + storedAccessToken);
    }
    const activeWorkspaceSelection = resolveWorkspaceHeaderForHost(readActiveWorkspaceSelection());
    if (activeWorkspaceSelection.length > 0) {
      requestConfiguration.headers.set('X-Workspace-Id', activeWorkspaceSelection);
    }
    requestConfiguration.headers.set('Accept-Language', resolveActiveLanguage());
    return requestConfiguration;
  });

  httpClient.interceptors.response.use(
    function passThroughResponse(response) {
      return response;
    },
    async function handleUnauthorizedResponse(requestError: unknown) {
      if (
        DOMAIN_WORKSPACE_MISMATCH_ERROR_CODES.some(function matchesCode(errorCode) {
          return isForbiddenResponseWithCode(requestError, errorCode);
        })
      ) {
        window.dispatchEvent(new Event(DOMAIN_WORKSPACE_MISMATCH_EVENT_NAME));
      }
      if (isForbiddenResponseWithCode(requestError, REQUIRED_CONSENTS_MISSING_ERROR_CODE)) {
        window.dispatchEvent(new Event(REQUIRED_CONSENTS_MISSING_EVENT_NAME));
      }
      if (isForbiddenResponseWithCode(requestError, LEGAL_ACCEPTANCE_PENDING_ERROR_CODE)) {
        window.dispatchEvent(new Event(LEGAL_ACCEPTANCE_PENDING_EVENT_NAME));
      }
      if (isForbiddenResponseWithCode(requestError, EMAIL_VERIFICATION_REQUIRED_ERROR_CODE)) {
        window.dispatchEvent(new Event(EMAIL_VERIFICATION_REQUIRED_EVENT_NAME));
      }
      if (
        axios.isAxiosError(requestError) === false ||
        requestError.response === undefined ||
        requestError.response.status !== 401
      ) {
        return Promise.reject(requestError);
      }
      const failedRequestConfiguration = requestError.config as RetryableRequestConfig | undefined;
      if (shouldAttemptTokenRefresh(failedRequestConfiguration) === false) {
        clearAuthenticationTokens();
        window.dispatchEvent(new Event(UNAUTHORIZED_EVENT_NAME));
        return Promise.reject(requestError);
      }
      try {
        await refreshAuthenticationSession();
      } catch (refreshError) {
        void refreshError;
        clearAuthenticationTokens();
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT_NAME));
        return Promise.reject(requestError);
      }
      const retryableRequestConfiguration = failedRequestConfiguration as RetryableRequestConfig;
      retryableRequestConfiguration.hasRetriedAfterTokenRefresh = true;
      return httpClient(retryableRequestConfiguration);
    }
  );

  return httpClient;
}

export const httpClient = createHttpClient();
